import { createContext, useContext, useState } from 'react';
import { Lang, translations } from '../i18n';
import { APP_CONFIG } from '../config/app.config';

const LangContext = createContext<
{ lang: Lang; 
  t: (key: keyof typeof translations['en'] | string) => string; 
  setLang: (lang: Lang) => void }>({
  lang: 'en',
  t: (k) => (translations['en'] as any)[k] ?? String(k),
  setLang: () => {}
});

// Get language from localStorage (settings)
function getStoredLang(): Lang {
  try {
    const settingsStr = localStorage.getItem(APP_CONFIG.STORAGE_KEYS.SETTINGS);
    if (settingsStr) {
      const settings = JSON.parse(settingsStr);
      return settings.language || APP_CONFIG.DEFAULT_LANGUAGE;
    }
  } catch (e) {
    console.error('Error reading language from settings:', e);
  }
  return APP_CONFIG.DEFAULT_LANGUAGE;
}

// Save language to localStorage (settings)
function saveStoredLang(lang: Lang) {
  try {
    const settingsStr = localStorage.getItem(APP_CONFIG.STORAGE_KEYS.SETTINGS);
    const settings = settingsStr ? JSON.parse(settingsStr) : {};
    settings.language = lang;
    settings.updatedAt = new Date().toISOString();
    localStorage.setItem(APP_CONFIG.STORAGE_KEYS.SETTINGS, JSON.stringify(settings));
  } catch (e) {
    console.error('Error saving language to settings:', e);
  }
}

export function LangProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>(getStoredLang());

  const setLang = (newLang: Lang) => {
    setLangState(newLang);
    saveStoredLang(newLang);
  };

  const t = (key: keyof typeof translations['en'] | string) => {
    const table = translations[lang] as Partial<typeof translations['en']>;
    return (table as any)[key] ?? (translations['en'] as any)[key] ?? String(key);
  };

  return (
    <LangContext.Provider value={{ lang, t, setLang }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang() {
  return useContext(LangContext);
}
